/**
 * Hydrometric Gauge detail panel (Flood Risk → "Hydrometric Gauges"; E.F.1 render,
 * operator-cop-doctrine §2.7 / §3B.8 ambient register). The side-panel body for a
 * clicked gauge dot — the latest WSC reading: water level, discharge, the sustained
 * rate-of-rise band, the served communities + their honest coverage, and provenance.
 *
 * Honesty (recon §C): there is NO official Canadian flood stage — the rate-of-rise is
 * TSR-derived; water level is station-local ASSUMED datum (within-station trend only,
 * NOT comparable across stations). A gauge-sparse station carries the recon §B
 * low-confidence-coverage note.
 */
import { useEffect } from 'react';
import type { HydrometricGaugeFeature } from './api';
import { gaugeBandStyle } from './styles/floodGauges';
import { logAction } from './lib/operatorActions';

function agoLabel(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  return `${Math.round(min / 60)}h ago`;
}

export function HydrometricGaugeBody({ gauge }: { gauge: HydrometricGaugeFeature }): JSX.Element {
  const p = gauge.properties;
  const [lon, lat] = gauge.geometry.coordinates;
  const band = gaugeBandStyle(p.rate_of_rise_cm_per_hr);

  useEffect(() => {
    logAction('click_utility_outage', { gauge: p.station_number, kind: 'hydrometric_gauge' });
    // Note: gauge clicks reuse the ambient-render click verb family; no dedicated
    // gauge verb (the gauge layer is read-only ambient, no operator decision).
  }, [p.station_number]);

  return (
    <div className="px-4 py-4 space-y-4" data-testid="hydrometric-gauge-body">
      <div className="border-l-2 pl-3" style={{ borderColor: band.color }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          🌊 Hydrometric gauge · {p.river ?? 'river'}
        </p>
        <h2 className="text-base font-semibold">{p.station_name ?? p.station_number}</h2>
        <div className="mt-1.5 flex items-center gap-2">
          <span
            className="text-[11px] px-1.5 py-0.5 rounded font-medium"
            style={{ backgroundColor: `${band.color}22`, color: band.color }}
          >
            {band.label}
          </span>
          {p.gauge_sparse && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300"
              title="Nearest gauge weakly covers a served community (recon §B)."
            >
              gauge-sparse
            </span>
          )}
        </div>
      </div>

      <section>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Rate of rise</dt>
          <dd className="font-mono font-semibold">
            {p.rate_of_rise_cm_per_hr != null ? `${p.rate_of_rise_cm_per_hr.toFixed(1)} cm/hr` : '—'}
          </dd>
          <dt className="text-slate-400">Water level</dt>
          <dd className="font-mono">{p.water_level_m != null ? `${p.water_level_m.toFixed(3)} m` : '—'}</dd>
          <dt className="text-slate-400">Discharge</dt>
          <dd className="font-mono">
            {p.discharge_cms != null ? `${p.discharge_cms.toLocaleString()} m³/s` : '—'}
          </dd>
          <dt className="text-slate-400">Station</dt>
          <dd className="font-mono text-xs">{p.station_number}</dd>
          <dt className="text-slate-400">Observed</dt>
          <dd>{agoLabel(p.observed_at)}</dd>
          <dt className="text-slate-400">Location</dt>
          <dd className="font-mono text-xs">
            {lat.toFixed(3)}, {lon.toFixed(3)}
          </dd>
        </dl>
      </section>

      {p.serves.length > 0 && (
        <section className="border-t border-slate-800 pt-3">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
            Serves · {p.serves.length}
          </h3>
          <ul className="text-sm space-y-0.5">
            {p.serves.map((s) => (
              <li key={s.name} className="flex items-center justify-between gap-2">
                <span className="truncate">{s.name}</span>
                <span
                  className={`font-mono text-xs shrink-0 ${
                    s.coverage === 'weak' || s.coverage === 'none' ? 'text-amber-400' : 'text-slate-400'
                  }`}
                >
                  {s.coverage}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="border-t border-slate-800 pt-3">
        <p className="text-[10px] text-slate-500 leading-relaxed">
          <span className="text-amber-400">No official Canadian flood stage exists.</span>{' '}
          {p.threshold_label ??
            'The rate-of-rise is TSR-derived; water level is station-local (ASSUMED datum) — within-station trend only, not comparable across stations.'}
        </p>
        <p className="text-[10px] text-slate-500 leading-relaxed mt-1">
          {p.attribution ?? 'Water Survey of Canada (ECCC) — GeoMet OGC API, real-time hydrometric.'}
        </p>
      </section>
    </div>
  );
}
